import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  untracked,
  viewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { MatDialog } from '@angular/material/dialog';
import { Router, RouterLink } from '@angular/router';
import { APP_PATHS, XEMEX_OID } from '@core/constants';
import { shouldOfferPlaceOnMap } from '@core/helpers/coordinate-placement.helper';
import { canViewInstallations } from '@core/helpers/installation-access.helper';
import { Installation, getUserFeaturePermissions, getUserInitialPartner } from '@core/models';
import { CoordinateResult } from '@core/models/location-coordinates.model';
import { TextButtonComponent } from '@custom-components/general/button';
import { CardComponent } from '@custom-components/general/card';
import { DetailFieldComponent } from '@custom-components/general/detail-field/detail-field.component';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { AuthStore } from '@root/app/store/auth.store';
import {
  InstallationAttributesStore,
  READS_PV_CONFIG,
} from '@root/app/store/installation-attributes.store';
import { InstallationDataSourcesStore } from '@root/app/store/installation-data-sources.store';
import { InstallationStore } from '@root/app/store/installation.store';
import { UserStore } from '@root/app/store/user.store';
import { BranchesMapComponent } from '../../../locations/components/branches-map/branches-map.component';
import {
  CoordinateMapDialogComponent,
  CoordinateMapDialogData,
  coordinateMapDialogConfig,
} from '../../../locations/components/coordinate-map-dialog/coordinate-map-dialog.component';
import { fedDevices, withDeviceDetails } from '../../fed-devices';
import { countryName, installationStreet } from '../../installation-labels';
import { InstallationActivityEmptyComponent } from '../installation-activity-empty/installation-activity-empty.component';
import { InstallationDevicesCardComponent } from '../installation-devices-card/installation-devices-card.component';
import { InstallationMeasurementPointsCardComponent } from '../installation-measurement-points-card/installation-measurement-points-card.component';
import { InstallationOwnershipCardComponent } from '../installation-ownership-card/installation-ownership-card.component';
import { InstallationRecentActivityComponent } from '../installation-recent-activity/installation-recent-activity.component';

@Component({
  selector: 'xemex-installation-general-tab',
  imports: [
    BranchesMapComponent,
    CardComponent,
    DetailFieldComponent,
    InstallationActivityEmptyComponent,
    InstallationDevicesCardComponent,
    InstallationMeasurementPointsCardComponent,
    InstallationOwnershipCardComponent,
    InstallationRecentActivityComponent,
    RouterLink,
    TextButtonComponent,
    TranslateModule,
  ],
  // Here rather than on the page, so the reads run only while this tab is shown.
  providers: [
    InstallationAttributesStore,
    InstallationDataSourcesStore,
    { provide: READS_PV_CONFIG, useValue: false },
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './installation-general-tab.component.html',
})
export class InstallationGeneralTabComponent {
  installation = input.required<Installation>();

  private readonly userStore = inject(UserStore);
  private readonly authStore = inject(AuthStore);
  private readonly installationStore = inject(InstallationStore);
  private readonly dataSources = inject(InstallationDataSourcesStore);
  private readonly dialog = inject(MatDialog);
  private readonly router = inject(Router);
  private readonly translate = inject(TranslateService);
  private readonly language = toSignal(this.translate.onLangChange, { initialValue: null });

  protected readonly APP_PATHS = APP_PATHS;

  protected readonly installationOid = computed(() => this.installation().installationOid);
  private readonly permissions = computed(() =>
    getUserFeaturePermissions(this.userStore.user(), this.authStore.permissions(), XEMEX_OID),
  );

  readonly street = computed(() => installationStreet(this.installation()));
  readonly country = computed(() =>
    countryName(
      this.installation().location.country,
      this.language()?.lang ?? this.translate.currentLang ?? 'en',
    ),
  );

  readonly hasCoordinates = computed(() => {
    const { latitude, longitude } = this.installation().location;
    return !!latitude && !!longitude;
  });

  // Moving the pin can move the installation's zone, so only Xemex staff may do it from here, and
  // only where the pin is the fallback: an address that could not be geocoded.
  readonly canPlaceOnMap = computed(() => {
    const { postalCode, streetNumber, latitude, longitude, coordinateSource } =
      this.installation().location;
    return (
      this.installation().status === 'Active' &&
      shouldOfferPlaceOnMap({ postalCode, streetNumber, latitude, longitude, coordinateSource }) &&
      getUserInitialPartner(this.userStore.user())?.partnerOid === XEMEX_OID &&
      this.permissions().includes('edit_location_details')
    );
  });

  readonly canOpenTabs = computed(() => this.permissions().includes('view_installations'));

  private readonly pointsCard = viewChild(InstallationMeasurementPointsCardComponent);
  private readonly pointsCardElement = viewChild(InstallationMeasurementPointsCardComponent, {
    read: ElementRef,
  });

  readonly feeding = computed(() => fedDevices(this.installation()));

  // The data sources name the devices at the location, which is where a device's type and status
  // come from; they are read only for a role that may open the Data sources tab as well.
  readonly locationDevices = computed(() => this.dataSources.dataSources()?.devices ?? []);
  readonly devices = computed(() => withDeviceDetails(this.feeding(), this.locationDevices()));
  readonly devicesLoading = computed(() => this.dataSources.isLoading());

  // The attributes are read from the device, so a role that may not see devices is not shown them
  // here either.
  readonly showAttributes = computed(() => {
    const user = this.userStore.user();
    const permissions = this.authStore.permissions();
    return (
      canViewInstallations(user, permissions) &&
      getUserFeaturePermissions(user, permissions, XEMEX_OID).includes('view_device')
    );
  });

  constructor() {
    effect(() => {
      const installationOid = this.installationOid();
      if (!this.canOpenTabs()) return;
      untracked(() => this.dataSources.load(installationOid));
    });
  }

  reloadInstallation(): void {
    this.installationStore.loadDetail(this.installation().installationOid);
  }

  // The installation carries the profile and points; the checklist is read apart from it.
  reloadBindings(): void {
    this.reloadInstallation();
    this.pointsCard()?.reload();
  }

  // A linked device can feed points and shows up among the devices at the location, so both are
  // read again.
  reloadAfterLink(): void {
    this.reloadBindings();
    this.dataSources.reload();
  }

  showProfile(): void {
    this.pointsCardElement()?.nativeElement.scrollIntoView({
      behavior: 'smooth',
      block: 'nearest',
    });
  }

  openActivity(): void {
    void this.router.navigate(['/', APP_PATHS.Installations, this.installationOid(), 'activity']);
  }

  // A save can change the installation's zone, so the page reads it again. So does a dialog that
  // found the location gone or no longer the user's.
  placeOnMap(): void {
    const installation = this.installation();
    const ref = this.dialog.open<
      CoordinateMapDialogComponent,
      CoordinateMapDialogData,
      CoordinateResult
    >(CoordinateMapDialogComponent, coordinateMapDialogConfig(installation.location.locationId));
    const editor = ref.componentInstance;
    ref.afterClosed().subscribe(result => {
      if (result === undefined && !editor.stale()) return;
      this.installationStore.loadDetail(installation.installationOid);
    });
  }
}
